import {isUserLoggedIn} from '@Libraries/user.library';
import {
  MAX_STORED_RECENT_SEARCHES,
  MAX_VISIBLE_RECENT_SEARCHES,
} from '@Panels/stock-panel/components/stock-panel-recent-searches-dropdown/stock-panel-recent-searches-dropdown.types';

const GET_RECENT_SEARCHES_URI = 'recentsearches/get';
const SAVE_RECENT_SEARCH_URI = 'recentsearches/save';
const CLEAR_RECENT_SEARCHES_URI = 'recentsearches/clear';

/**
 * Persistent in-memory cache of the user's recent searches (newest first). The server (DynamoDB
 * behind a Redis cache) is the source of truth; this cache is fetched once per page and updated
 * optimistically on save/clear so the dropdown never waits on the network. Every accessor/mutator
 * returns the current list so the consuming component can mirror it into React state — the cache
 * itself is not reactive, the component's `useState` is.
 */
let recentSearches: string[] | null = null;

const toStringArray = (response: unknown): string[] => {
  if (!Array.isArray(response)) {
    return [];
  }
  return response.filter((item): item is string => typeof item === 'string');
};

export const loadRecentSearches = async (): Promise<string[]> => {
  if (recentSearches !== null) {
    return recentSearches;
  }
  if (!isUserLoggedIn()) {
    recentSearches = [];
    return recentSearches;
  }
  try {
    const fetched = toStringArray(await window.PMW.readLocal(GET_RECENT_SEARCHES_URI));
    // A save/clear may have populated the cache while this request was in flight — don't clobber it.
    recentSearches ??= fetched;
  } catch {
    recentSearches ??= [];
  }
  return recentSearches;
};

export const getMatchingRecentSearches = (allSearches: string[], searchText: string): string[] => {
  const needle = searchText.trim().toLowerCase();
  const matches = needle ? allSearches.filter((term) => term.toLowerCase().includes(needle)) : allSearches;
  return matches.slice(0, MAX_VISIBLE_RECENT_SEARCHES);
};

export const saveRecentSearch = (term: string): string[] => {
  const trimmedTerm = term.trim();
  if (!trimmedTerm || !isUserLoggedIn()) {
    return recentSearches ?? [];
  }
  const otherSearches = (recentSearches ?? []).filter((existingTerm) => existingTerm.toLowerCase() !== trimmedTerm.toLowerCase());
  recentSearches = [trimmedTerm, ...otherSearches].slice(0, MAX_STORED_RECENT_SEARCHES);
  void window.PMW.writeLocal(SAVE_RECENT_SEARCH_URI, {term: trimmedTerm});
  return recentSearches;
};

export const clearRecentSearches = (): string[] => {
  recentSearches = [];
  if (isUserLoggedIn()) {
    void window.PMW.writeLocal(CLEAR_RECENT_SEARCHES_URI);
  }
  return recentSearches;
};
